import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import { useCurrentTime } from "../../shared/use-current-time";
import BatteryIcon from "./terminal-preview/battery.svg?react";
import {
	getLogoColour,
	getStatusBarColour,
} from "./terminal-preview/colour.utils";
import t1ApprovedScreen from "./terminal-preview/t1-approved-screen.png";
import terminalShellBlackT1 from "./terminal-preview/t1-shell-black.png";
import t1TapNowScreen from "./terminal-preview/t1-tap-now-screen.png";
import t2ApprovedScreen from "./terminal-preview/t2-approved-screen.png";
import terminalShellBlackT2 from "./terminal-preview/t2-shell-black.png";
import t2TapNowScreen from "./terminal-preview/t2-tap-now-screen.png";
import WifiIcon from "./terminal-preview/wifi-signal.svg?react";
import ZellerLogo from "./terminal-preview/zeller-logo-no-fill.svg?react";

const MAX_IMAGE_HEIGHT = 992;
const MAX_IMAGE_WIDTH = 640;

export namespace TerminalPreview {
	export type Props = {
		model: string;
		terminalColour: string | undefined;
		terminalLogoUrl: string | undefined;
		className?: string;
	};
}

export function TerminalPreview({
	model,
	terminalColour = "#212322", // BLACK_900,
	terminalLogoUrl,
	className,
}: TerminalPreview.Props) {
	const isT2 = model === "T2";

	const statusBarColour = getStatusBarColour(terminalColour);
	const logoColour = getLogoColour(terminalColour);

	const [isTerminalShellLoaded, setIsTerminalShellLoaded] = useState(false);
	const terminalShellRef = useRef<HTMLImageElement | null>(null);
	const [logoHasRoundedCorners, setLogoHasRoundedCorners] = useState(false);
	const [isLogoLoaded, setIsLogoLoaded] = useState(
		terminalLogoUrl ? false : true,
	);
	const logoRef = useRef<HTMLImageElement | null>(null);
	const currentTime = useCurrentTime();
	const shouldShowTerminal =
		(isTerminalShellLoaded && !terminalLogoUrl) || isLogoLoaded;

	useEffect(() => {
		// NOTE: Handles case where the image is in cache so onLoad isn't called
		if (terminalShellRef.current?.complete) {
			setIsTerminalShellLoaded(true);
		}

		if (logoRef.current?.complete) {
			setIsLogoLoaded(true);
		}
	}, []);

	return (
		<div className={className}>
			<div
				className={twMerge(
					"relative",
					"flex flex-col",
					"w-[260px] h-[360px]",
					[
						"transition-opacity duration-300 ease-in-out",
						shouldShowTerminal ? "opacity-100" : "opacity-0",
					],
					isT2 && "pl-10",
				)}
			>
				<img
					src={isT2 ? terminalShellBlackT2 : terminalShellBlackT1}
					fetchPriority="high"
					ref={terminalShellRef}
					alt="terminal shell"
					className={twMerge(
						"absolute",
						isT2 ? "z-10 w-[150px] -translate-x-[10px]" : "z-0 w-[198px]",
					)}
					width={198}
					height={360}
					onLoad={() => {
						setIsTerminalShellLoaded(true);
					}}
				/>
				<div
					className={twMerge(
						"font-bold absolute flex scale-[0.75] origin-top-left transform flex-col items-center justify-center transition-all ease-in-out",
						isT2
							? "z-0 h-[396px] w-[180px] translate-y-[17px] translate-x-[-2.5px] shadow-[8px_8px_64px_0px_rgba(0,0,0,0.24)]"
							: "z-10 h-[318px] w-[181px] translate-y-[80px] translate-x-[8px] rounded-[1px] shadow-[0_0_1px_1px_#151515]",
					)}
					style={{ backgroundColor: terminalColour }}
					aria-label="terminal screen"
				>
					<div
						className={twMerge(
							"absolute top-1 z-20 flex h-[12px] w-full flex-row justify-between gap-4 px-12 py-2",
						)}
						style={{ color: statusBarColour }}
						aria-label="terminal status bar"
					>
						<span className="font-semibold text-[7px]/[8px]">
							{currentTime}
						</span>
						<div className="flex gap-1">
							<WifiIcon
								width={8}
								height={8}
								fill={statusBarColour}
								style={{ color: statusBarColour }}
							/>
							<BatteryIcon width={4} height={8} fill={statusBarColour} />
						</div>
					</div>

					{terminalLogoUrl ? (
						<>
							<img
								className={twMerge(
									"max-height-[798px] w-auto max-w-[640px] scale-[0.25] transform",
									isT2 ? "max-height-[996px]" : "max-height-[1240px]",
									logoHasRoundedCorners ? "rounded-[32px]" : "",
								)}
								src={terminalLogoUrl}
								alt="business logo"
								onLoad={(e) => {
									const image = e.target as HTMLImageElement;
									if (
										image.width >= MAX_IMAGE_WIDTH ||
										image.height >= MAX_IMAGE_HEIGHT
									) {
										setLogoHasRoundedCorners(true);
									} else {
										setLogoHasRoundedCorners(false);
									}

									setIsLogoLoaded(true);
								}}
								ref={logoRef}
							/>

							<div className="absolute bottom-16 opacity-50">
								<ZellerLogo
									height={14}
									width={40}
									style={{ color: logoColour }}
								/>
							</div>
						</>
					) : (
						<ZellerLogo height={24} width={69} style={{ color: logoColour }} />
					)}

					<div className="absolute h-full w-full">
						<img
							className="absolute z-30 h-full w-full animate-terminalApprovedScreen rounded-[1px] shadow-[0_0_1px_1px_#151515,0_0_0_1px_#151515_inset]"
							src={isT2 ? t2ApprovedScreen : t1ApprovedScreen}
							alt="Approved screen"
						/>
						<img
							className="absolute z-30 h-full w-full animate-terminalTapNowScreen rounded-[1px] shadow-[0_0_1px_1px_#151515,0_0_0_1px_#151515_inset]"
							src={isT2 ? t2TapNowScreen : t1TapNowScreen}
							alt="Tap now screen"
						/>
						<div className="absolute z-40 h-full w-full rounded-[1px] shadow-[0_0_0_0.5px_#151515_inset]" />
					</div>
				</div>
			</div>
		</div>
	);
}
